import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import { useGetSchedulerFunnelProfilesId } from '@/utils/scheduler/funnel/hooks/useGetSchedulerFunnelProfilesId';
import { useNewFunnelActivated } from '@/utils/scheduler/funnel/hooks/useNewFunnelActivated';

import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

import PropTypes from 'prop-types';

import { tabsList } from '@/lib/scheduler/tabs';
// Lib
import { INSTAGRAM } from '@/lib/socialProfile';
import { ADMIN_ROLE, MANAGER_ROLE } from '@/lib/user/roles';

import { ButtonFunnelModal } from '@/components/pages/scheduler/SchedulerHeader/ButtonFunnelModal';
import { ButtonUnlockInstagramPermission } from '@/components/pages/scheduler/SchedulerHeader/ButtonUnlockInstagramPermission';
import { OldButtonFunnelModal } from '@/components/pages/scheduler/SchedulerHeader/OldButtonFunnelModal';
import { UnlockSchedulerPermission } from '@/components/pages/scheduler/SchedulerHeader/UnlockSchedulerPermission';
// Components
import { PageHeaderRecap } from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap';

const OldHeader = ({
    activeSocialProfile,
    hasSchedulerPermission = true,
    setNeedReloadScheduledPosts = () => {}
}) => {
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const { user } = useUser();
    const instagramNoPermissions =
        !hasSchedulerPermission && activeSocialProfile?.social_network_type === INSTAGRAM;
    const createPostIsActive = hasSchedulerPermission || instagramNoPermissions;

    useEffect(() => {
        router.replace(deleteQuery(['step', 'scheduler_permission']), undefined, { shallow: true });
    }, []);

    if (!createPostIsActive) {
        return null;
    }

    return (
        <div className="hidden w-full items-center justify-end space-x-3 md:flex lg:mt-0 lg:w-auto lg:flex-1">
            {instagramNoPermissions &&
            (user.acl_role === ADMIN_ROLE || user.acl_role === MANAGER_ROLE) ? (
                <ButtonUnlockInstagramPermission />
            ) : null}
            <OldButtonFunnelModal setNeedReloadScheduledPosts={setNeedReloadScheduledPosts} />
        </div>
    );
};

const NewHeader = ({
    isGroup,
    activeGroup,
    activeSocialProfile,
    setNeedReloadScheduledPosts = () => {}
}) => {
    // Router
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();

    const { profilesIdWithPermission, profilesId } = useGetSchedulerFunnelProfilesId({
        activeGroup,
        activeSocialProfile
    });

    useEffect(() => {
        router.replace(deleteQuery('scheduler_permission'), undefined, { shallow: true });
    }, []);

    const displayButtonPermission = profilesIdWithPermission.length !== profilesId.length;

    return (
        <div className="hidden w-full items-center justify-end space-x-3 md:flex lg:mt-0 lg:w-auto lg:flex-1">
            {displayButtonPermission ? (
                <UnlockSchedulerPermission activeSocialProfile={activeSocialProfile} />
            ) : null}
            <ButtonFunnelModal
                isGroup={isGroup}
                activeGroup={activeGroup}
                activeSocialProfile={activeSocialProfile}
                setNeedReloadScheduledPosts={setNeedReloadScheduledPosts}
            />
        </div>
    );
};

const SchedulerHeader = ({
    isGroup,
    activeGroup,
    activeSocialProfile,
    hasSchedulerPermission = true,
    setNeedReloadScheduledPosts = () => {}
}) => {
    const { t } = useTranslation('scheduler');
    const pathname = usePathname();
    const { isNewFunnel } = useNewFunnelActivated();

    const currentLink = tabsList.find((link) => link.url === pathname);

    return (
        <PageHeaderRecap hideStickyHeader title={t(currentLink.label)}>
            {() => {
                if (isNewFunnel) {
                    return (
                        <NewHeader
                            isGroup={isGroup}
                            activeGroup={activeGroup}
                            activeSocialProfile={activeSocialProfile}
                            setNeedReloadScheduledPosts={setNeedReloadScheduledPosts}
                        />
                    );
                }

                return (
                    <OldHeader
                        activeSocialProfile={activeSocialProfile}
                        hasSchedulerPermission={hasSchedulerPermission}
                        setNeedReloadScheduledPosts={setNeedReloadScheduledPosts}
                    />
                );
            }}
        </PageHeaderRecap>
    );
};

SchedulerHeader.propTypes = {
    hasSchedulerPermission: PropTypes.bool,
    setNeedReloadScheduledPosts: PropTypes.func
};

export default SchedulerHeader;
